/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './BankVerifyHeroDemo.module.scss';

import type { BankVerifyHeroDemoProps } from './BankVerifyHeroDemo.types';

type Screen = 'form' | 'result' | 'deeper';

const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.3l2.1 2.1L9.5 3.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IBAN = 'GB29 NWBK 6016 1331 9268 19';
const NAME = 'Acme Trading Ltd';

const ACCOUNT_SUMMARY: { key: string; value: string; mono?: boolean; pill?: boolean }[] = [
  { key: 'Country', value: '🇬🇧 GB' },
  { key: 'Account name', value: 'Acme Trading Ltd' },
  { key: 'Account identifier', value: '•••• •••• 2184', mono: true },
  { key: 'Account status', value: 'ACTIVE', pill: true },
  { key: 'Name check status', value: 'FULL MATCH' },
  { key: 'Verification scheme', value: 'SCAN' },
];

const DEEPER_ROWS: { icon: string; title: string; text: string; monitor?: boolean }[] = [
  {
    icon: '🏢',
    title: 'Check the company information',
    text: 'Legal name, status, registration — from the official registry',
  },
  { icon: '👥', title: 'Identify shareholders & UBOs', text: 'Owners with exact percentages, directors and officers' },
  { icon: '📊', title: 'Review financial statements', text: 'Up to 20 years of balance sheet, P&L and ratios' },
  { icon: '🛡', title: 'Screen for PEPs & sanctions', text: 'Every UBO and director checked against global lists' },
  { icon: '🔔', title: 'Add to monitoring', text: 'Get alerted when ownership or status changes', monitor: true },
];

export const BankVerifyHeroDemo = ({ className }: BankVerifyHeroDemoProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { threshold: 0.35 });

  const runIdRef = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [screen, setScreen] = useState<Screen>('form');
  const [ibanText, setIbanText] = useState('');
  const [nameText, setNameText] = useState('');
  const [focusField, setFocusField] = useState<'iban' | 'name' | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const [monitorOn, setMonitorOn] = useState(false);
  const [done, setDone] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const wait = useCallback(
    (ms: number, id: number) =>
      new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => (runIdRef.current === id ? resolve() : reject()), ms);
        timers.current.push(t);
      }),
    [],
  );

  const showFinalState = useCallback(() => {
    clearTimers();
    setScreen('deeper');
    setIbanText(IBAN);
    setNameText(NAME);
    setFocusField(null);
    setVerifying(false);
    setRevealed(DEEPER_ROWS.length);
    setMonitorOn(true);
    setDone(true);
  }, [clearTimers]);

  const play = useCallback(async () => {
    clearTimers();
    const id = ++runIdRef.current;

    setScreen('form');
    setIbanText('');
    setNameText('');
    setFocusField(null);
    setVerifying(false);
    setRevealed(0);
    setMonitorOn(false);
    setDone(false);

    try {
      await wait(650, id);

      setFocusField('iban');
      for (let i = 1; i <= IBAN.length; i += 1) {
        await wait(38, id);
        setIbanText(IBAN.slice(0, i));
      }

      await wait(320, id);
      setFocusField('name');
      for (let i = 1; i <= NAME.length; i += 1) {
        await wait(55, id);
        setNameText(NAME.slice(0, i));
      }

      await wait(420, id);
      setFocusField(null);
      setVerifying(true);
      await wait(1050, id);

      setVerifying(false);
      setScreen('result');
      await wait(2800, id);

      setScreen('deeper');
      for (let i = 1; i <= DEEPER_ROWS.length; i += 1) {
        await wait(280, id);
        setRevealed(i);
      }

      await wait(700, id);
      setMonitorOn(true);
      await wait(600, id);
      setDone(true);
    } catch {
      // superseded run — ignore
    }
  }, [clearTimers, wait]);

  useEffect(() => {
    if (reduced) {
      showFinalState();

      return;
    }

    if (inView) {
      play();
    }

    return clearTimers;
  }, [inView, reduced, play, showFinalState, clearTimers]);

  const replay = useCallback(() => {
    if (reduced) return;
    play();
  }, [reduced, play]);

  return (
    <div ref={ref} className={cn(styles.stage, className)} aria-label="Bank account verification demo">
      <div className={styles.demo}>
        <div className={styles.head}>
          <span className={styles.dots}>
            <i />
            <i />
            <i />
          </span>
          <span className={styles.headTitle}>globaldatabase.com — verify</span>
          <span className={styles.live}>LIVE</span>
        </div>

        {screen === 'form' && (
          <div className={styles.screen}>
            <div className={styles.fGroup}>
              <span className={styles.fLabel}>Country</span>
              <div className={styles.fField}>
                <span>🇪🇺</span>
                <span>European Payments Council (EPC)</span>
                <span className={styles.chev}>⌄</span>
              </div>
            </div>

            <div className={styles.fGroup}>
              <span className={styles.fLabel}>
                IBAN <span className={styles.req}>*</span>
              </span>
              <div className={cn(styles.fField, focusField === 'iban' && styles.fFieldFocus)}>
                {ibanText ? (
                  <span className={styles.fValue}>{ibanText}</span>
                ) : (
                  <span className={styles.fPlaceholder}>IBAN</span>
                )}
                {focusField === 'iban' && <span className={styles.cursor} />}
              </div>
              <div className={styles.fHint}>5 to 34 characters</div>
            </div>

            <div className={styles.fGroup}>
              <span className={styles.fLabel}>
                Bank account name <span className={styles.req}>*</span>
              </span>
              <div className={cn(styles.fField, focusField === 'name' && styles.fFieldFocus)}>
                {nameText ? (
                  <span className={styles.fValue}>{nameText}</span>
                ) : (
                  <span className={styles.fPlaceholder}>Bank account name</span>
                )}
                {focusField === 'name' && <span className={styles.cursor} />}
              </div>
            </div>

            <div className={cn(styles.fBtn, verifying && styles.fBtnPressed)}>
              {verifying && <span className={styles.spinner} />}
              {verifying ? 'Verifying…' : 'Verify Bank Account'}
            </div>
          </div>
        )}

        {screen === 'result' && (
          <div className={styles.screen}>
            <div className={styles.banner}>
              <span className={styles.bannerCheck}>
                <CheckIcon size={12} />
              </span>
              <div className={styles.bannerCopy}>
                <h3>Account verified — Name matched</h3>
                <div className={styles.bannerTime}>Verified just now</div>
              </div>
              <span className={styles.trust}>
                TRUST SCORE: <b>HIGH</b>
              </span>
            </div>

            <div className={styles.rCard}>
              <div className={styles.rCardLabel}>Account data summary</div>
              <div className={styles.rGrid}>
                {ACCOUNT_SUMMARY.map((item) => (
                  <div key={item.key} className={styles.rItem}>
                    <div className={styles.rItemKey}>{item.key}</div>
                    {item.pill ? (
                      <div className={styles.rItemValue}>
                        <span className={styles.rPill}>{item.value}</span>
                      </div>
                    ) : (
                      <div className={cn(styles.rItemValue, item.mono && styles.rItemValueMono)}>{item.value}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.rNote}>
              Sensitive account details are masked. Full results available in the platform and API.
            </div>
          </div>
        )}

        {screen === 'deeper' && (
          <div className={styles.screen}>
            <div className={styles.dpHead}>
              <span className={styles.dpCheck}>
                <CheckIcon size={13} />
              </span>
              <div>
                <b>You{"'"}ve verified the bank account.</b>
                <span>
                  Great — now let{"'"}s check who{"'"}s behind it: <b>Acme Trading Ltd</b>
                </span>
              </div>
            </div>

            <div className={styles.dpList}>
              {DEEPER_ROWS.map((row, i) => (
                <div
                  key={row.title}
                  className={cn(
                    styles.dpRow,
                    i < revealed && styles.dpRowShow,
                    row.monitor && monitorOn && styles.dpRowArmed,
                  )}
                >
                  <span className={styles.dpIco}>{row.icon}</span>
                  <div className={styles.dpRowCopy}>
                    <b>{row.title}</b>
                    <span>
                      {row.monitor && monitorOn ? 'Monitoring active — you’ll be alerted on changes' : row.text}
                    </span>
                  </div>
                  {row.monitor ? (
                    <span className={cn(styles.dpToggle, monitorOn && styles.dpToggleOn)}>
                      <i />
                    </span>
                  ) : (
                    <span className={styles.dpChev}>›</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {done && !reduced && (
          <button type="button" className={styles.replay} onClick={replay}>
            ↻ Replay
          </button>
        )}
      </div>
    </div>
  );
};
